import React, { useState } from 'react';
import WorkflowProgress from '../components/WorkflowProgress';
import WorkflowSummary from '../components/WorkflowSummary';
import CampaignSummary from '../components/CampaignSummary';
import ReportViewer from '../components/ReportViewer';
import ExportDataDialog from '../components/ExportDataDialog';
import DiagnosticPanel from '../components/DiagnosticPanel';
import WorkflowDebugPanel from '../components/WorkflowDebugPanel';
import { CampaignFormData } from '../types';
import { useWorkflowPage } from '../hooks/useWorkflowPage';
import { useWorkflowReportActions } from '../hooks/useWorkflowReportActions';
import { Download, AlertTriangle, RefreshCw, Settings } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface WorkflowPageProps {
  formData: CampaignFormData;
  userId: string;
  onReset: () => void;
}

const WorkflowPage: React.FC<WorkflowPageProps> = ({ formData, userId, onReset }) => {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  
  const {
    showSummary,
    setShowSummary,
    selectedReport,
    setSelectedReport,
    isGeneratingConsolidated,
    setIsGeneratingConsolidated,
    consolidatedReport,
    setConsolidatedReport,
    workflowAnalysis,
    restartAnalysis,
    initializationError,
    isInFallbackMode,
    isInitializing
  } = useWorkflowPage({ formData, userId });

  const { campaignId, currentStep, steps } = workflowAnalysis;

  const reportActions = useWorkflowReportActions({
    formData,
    steps,
    campaignId,
    currentStep,
    consolidatedReport,
    setConsolidatedReport,
    setSelectedReport,
    setIsGeneratingConsolidated
  });

  // Show loading state during initialization
  if (isInitializing) {
    return (
      <div className="space-y-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center">
            <RefreshCw className="h-6 w-6 text-blue-600 mr-3 animate-spin" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Initializing Campaign Analysis</h3>
              <p className="text-blue-700">Setting up your campaign data and preparing analysis workflow...</p>
              <div className="mt-4 text-sm text-blue-600">
                This may take a moment. Please don't refresh the page.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if initialization failed
  if (initializationError) {
    return (
      <div className="space-y-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-red-800 mb-2">Initialization Error</h3>
              <p className="text-red-700 mb-4">{initializationError}</p>
              <div className="flex space-x-4">
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <RefreshCw size={16} />
                  Refresh Page
                </button>
                <button
                  onClick={() => setShowDiagnostics(true)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center gap-2"
                >
                  <Settings size={16} />
                  Diagnostics
                </button>
                <button
                  onClick={onReset}
                  className="px-4 py-2 bg-neutral-600 text-white rounded-md hover:bg-neutral-700 transition-colors"
                >
                  Start Over
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {showDiagnostics && (
          <DiagnosticPanel onReset={onReset} />
        )}
      </div>
    );
  }

  const handleExportCurrentData = () => {
    // Check if there's meaningful data to export
    const hasData = formData.core_issue_details.campaign_name || 
                   formData.core_issue_details.political_issue ||
                   formData.campaign_outcome.winning_aspiration;
    
    if (!hasData) {
      toast.error('No campaign data to export. Please fill in some form fields first.');
      return;
    }
    
    setShowExportDialog(true);
  };

  const handleViewResult = (stepId: number) => {
    const step = steps.find(step => step.id === stepId);
    if (step?.result) {
      setSelectedReport({
        title: step.title,
        content: step.result
      });
    }
  };

  const handleRestartAnalysis = async () => {
    setShowSummary(false);
    setSelectedReport(null);
    setConsolidatedReport(null);
    await restartAnalysis();
  };

  if (showSummary) {
    return (
      <>
        {/* Fallback mode notification */}
        {isInFallbackMode && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-orange-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-orange-800">Local Storage Mode</h3>
                  <p className="text-sm text-orange-700">Your data is being saved locally due to connection issues.</p>
                </div>
              </div>
              <button
                onClick={() => setShowDiagnostics(!showDiagnostics)}
                className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700"
              >
                <Settings size={14} />
              </button>
            </div>
          </div>
        )}
        
        {showDiagnostics && (
          <DiagnosticPanel onReset={onReset} />
        )}
        
        <WorkflowSummary
          formData={formData}
          steps={steps}
          consolidatedReport={consolidatedReport}
          selectedReport={selectedReport}
          isGeneratingConsolidated={isGeneratingConsolidated}
          onViewResult={handleViewResult}
          onGenerateConsolidatedReport={reportActions.handleGenerateReport}
          onDownloadAllReports={reportActions.handleDownloadReports}
          onRestartAnalysis={handleRestartAnalysis}
          onReset={onReset}
          onCloseReport={() => setSelectedReport(null)}
          onShowConsolidatedReport={reportActions.handleShowConsolidatedReport}
        />
        
        <ExportDataDialog
          isOpen={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          formData={formData}
        />
      </>
    );
  }

  return (
    <div className="space-y-8">
      {/* Fallback mode notification */}
      {isInFallbackMode && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-orange-600 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-orange-800">Local Storage Mode</h3>
                <p className="text-sm text-orange-700">Your data is being saved locally due to connection issues.</p>
              </div>
            </div>
            <button
              onClick={() => setShowDiagnostics(!showDiagnostics)}
              className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 flex items-center gap-1"
            >
              <Settings size={14} />
              Diagnostics
            </button>
          </div>
        </div>
      )}

      {showDiagnostics && (
        <DiagnosticPanel onReset={onReset} />
      )}

      {/* Export Button - Prominently placed at the top */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-yellow-800">Campaign Data Export</h3>
            <p className="text-sm text-yellow-700">Export your current campaign data for backup or transfer to a new project</p>
          </div>
          <button
            onClick={handleExportCurrentData}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Download size={16} />
            Export Campaign Data
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <CampaignSummary
          formData={formData}
          isComplete={workflowAnalysis.isComplete}
          onShowSummary={() => setShowSummary(true)}
        />

        <div className="lg:col-span-2">
          <WorkflowProgress
            steps={steps}
            currentStep={currentStep}
            onViewResult={handleViewResult}
          />
        </div>
      </div>
      
      {selectedReport && (
        <ReportViewer
          title={selectedReport.title}
          content={selectedReport.content}
          onClose={() => setSelectedReport(null)}
        />
      )}

      <ExportDataDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        formData={formData}
      />

      <WorkflowDebugPanel
        formData={formData}
        campaignId={campaignId}
        currentStep={currentStep}
        steps={steps}
        isProcessing={workflowAnalysis.isProcessing}
        isComplete={workflowAnalysis.isComplete}
        shouldStartAnalysis={workflowAnalysis.shouldStartAnalysis}
        onRestartAnalysis={handleRestartAnalysis}
        onForceProcessStep={workflowAnalysis.processStep}
      />
    </div>
  );
};

export default WorkflowPage;
